/**
 * DecisionFlow — PlatformAdminShell
 * ui/src/shells/PlatformAdminShell.jsx
 * OI-S225-01
 *
 * Shell for user_type = "tool_admin" | "platform_admin".
 * Route root: /platform-admin/
 *
 * Route tree:
 *   /platform-admin           → redirect → /platform-admin/tenants
 *   /platform-admin/tenants   → TenantList
 *   /platform-admin/provision → TenantProvision
 *
 * Auth: session cookie — same as all other shells.
 * require_roles("tool_admin") on every §19 endpoint reads from the JWT
 * embedded in the cookie. No Bearer token needed.
 *
 * Design rules (non-negotiable, §15):
 *   - No dark: prefix
 *   - All text sizes text-tok-*
 *   - All <td> text-[rgb(var(--color-text))]
 *   - All <input>/<select> bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))]
 *   - .skeleton for loading states
 *   - useTranslation — no hardcoded English strings
 *   - TopBar: no dark/setDark props
 *   - Shell: no dark/setDark state — ThemeButton is self-contained
 */

import { useState } from "react";
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeDollarSign, BarChart3, Building2, PlusCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../auth/AuthContext.jsx";
import TopBar from "../components/shared/TopBar.jsx";
import TenantList from "../components/platform-admin/TenantList.jsx";
import TenantProvision from "../components/platform-admin/TenantProvision.jsx";
import OpsDashboard from "../components/platform-admin/OpsDashboard.jsx";
import TierPricing from "../components/platform-admin/TierPricing.jsx";
import TenantDetail from "../components/platform-admin/TenantDetail.jsx";




// ---------------------------------------------------------------------------
// Nav structure
// ---------------------------------------------------------------------------

const NAV_ITEMS = [
  { labelKey: "nav.tenants",    icon: Building2,   to: "/platform-admin/tenants"    },
  { labelKey: "nav.provision",  icon: PlusCircle,  to: "/platform-admin/provision"  },
  { labelKey: "nav.ops",        icon: BarChart3,   to: "/platform-admin/ops"        },
  { labelKey: "nav.pricing",    icon: BadgeDollarSign, to: "/platform-admin/pricing" },
];

// ---------------------------------------------------------------------------
// PlatformAdminNav — matches CompAdminNav sidebar pattern exactly
// ---------------------------------------------------------------------------

function PlatformAdminNav({ mobileOpen, onClose }) {

  const { t } = useTranslation("platform");

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-56 overflow-y-auto border-r border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pt-16 lg:static lg:pt-0 transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <nav className="p-3 space-y-4">
          <div>
            <div className="mb-1 px-2 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
              {t("nav.group")}
            </div>
            {NAV_ITEMS.map(({ labelKey, icon: Icon, to }) => (
              <NavLink
                key={to}
                to={to}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-tok-sm font-medium transition ${
                    isActive
                      ? "bg-[rgb(var(--color-primary))] text-white"
                      : "text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                  }`
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate flex-1">{t(labelKey)}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// PlatformAdminContextHeader — page title + breadcrumb
// ---------------------------------------------------------------------------

function PlatformAdminContextHeader({ userName, userRole }) {
  const { t } = useTranslation("platform");
  const location = useLocation();

  const activeItem = NAV_ITEMS.find((item) => location.pathname === item.to);
  const pageTitle  = activeItem ? t(activeItem.labelKey) : t("nav.group");

  return (
    <div className="mb-4 space-y-1">
      <div className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
        <span>{t("nav.group")}</span>
      </div>
      <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">
        {pageTitle}
      </h1>
      <div className="flex flex-wrap items-center gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
        {userName && <span>{userName}</span>}
        {userRole && <><span>·</span><span>{userRole}</span></>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel router
// ---------------------------------------------------------------------------

function PlatformAdminPanel() {
  return (
    <Routes>
      <Route index element={<Navigate to="tenants" replace />} />
      <Route path="tenants"          element={<TenantList />} />
      <Route path="tenants/:tenantId" element={<TenantDetail />} />
      <Route path="provision"        element={<TenantProvision />} />
      <Route path="ops"              element={<OpsDashboard />} />
      <Route path="pricing"          element={<TierPricing />} />
      <Route path="*"                element={<Navigate to="tenants" replace />} />
    </Routes>
  );
}

// ---------------------------------------------------------------------------
// PlatformAdminShell root
// ---------------------------------------------------------------------------

export default function PlatformAdminShell() {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const userName = user?.full_name ?? user?.email ?? null;
  const userRole = user?.user_type ?? null;

  return (
    <div className="min-h-screen bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))]">
      <TopBar
        userName={userName}
        userRole={userRole}
        onMenuOpen={() => setMobileOpen(true)}
      />

      <div className="flex min-h-[calc(100vh-64px)] min-w-0">
        <PlatformAdminNav
          mobileOpen={mobileOpen}
          onClose={() => setMobileOpen(false)}
        />

        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <PlatformAdminContextHeader
            userName={userName}
            userRole={userRole}
          />

          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <PlatformAdminPanel />
          </motion.div>
        </main>
      </div>
    </div>
  );
}
